/**
 * Shared CSS for the `_co_onb` dialog family: the template-copy prompt, the
 * welcome dialog and the tour card. One exported template string so each
 * host injects it once and steps opt into named animation classes.
 *
 * Conventions
 * -----------
 * • Every class is namespaced `_co_onb_*` to avoid global collisions.
 * • Tokens (`--co-*`) are scoped to `._co_onb` so light/dark switching
 *   is a single descendant rule swap (`.dark ._co_onb { ... }`).
 * • Settle classes 1–4 are the staggered entrance staircase used by all
 *   steps. Steps reference them by class name and don't redeclare them.
 * • Step exits are driven by `_co_onb_step_exit` on the step container.
 *   Exit duration must match `STEP_EXIT_MS` in the tour card.
 */

export const PROMPT_DIALOG_STYLES = `
  ._co_onb {
    --co-ink: #1A1A16;
    --co-paper: #F3EFE6;
    --co-n50: #65604F;
    --co-n60: #87806F;
    --co-plum: #6E5065;
    --co-rust: #B86E49;

    --co-fg: var(--co-ink);
    --co-fg-muted: var(--co-n50);
    --co-border: rgba(15, 12, 8, 0.14);
    --co-slot-stroke: rgba(15, 12, 8, 0.22);
    --co-slot-stroke-armed: rgba(15, 12, 8, 0.55);
    --co-track: rgba(15, 12, 8, 0.18);
    --co-track-strong: rgba(15, 12, 8, 0.32);
    --co-cta-fg: #FBF9F4;
    --co-cta-shadow: 0 1px 2px rgba(15,12,8,0.04);
    /* Snapped (locked) — neutral glass */
    --co-snap-fill: rgba(15, 12, 8, 0.06);
    --co-snap-fill-hover: rgba(15, 12, 8, 0.10);
    --co-snap-glow: 0 6px 24px rgba(15, 12, 8, 0.10),
                    0 1px 2px rgba(15, 12, 8, 0.06);
    --co-snap-ring: rgba(15, 12, 8, 0.32);
    --co-snap-border: rgba(15, 12, 8, 0.22);
  }
  .dark ._co_onb {
    --co-fg: #F3EFE6;
    --co-fg-muted: #87806F;
    --co-border: rgba(255, 247, 230, 0.18);
    --co-slot-stroke: rgba(255, 247, 230, 0.28);
    --co-slot-stroke-armed: rgba(255, 247, 230, 0.65);
    --co-track: rgba(255, 247, 230, 0.20);
    --co-track-strong: rgba(255, 247, 230, 0.36);
    --co-snap-fill: rgba(255, 247, 230, 0.10);
    --co-snap-fill-hover: rgba(255, 247, 230, 0.16);
    --co-snap-glow: 0 8px 28px rgba(0, 0, 0, 0.36),
                    0 1px 2px rgba(0, 0, 0, 0.20);
    --co-snap-ring: rgba(255, 247, 230, 0.45);
    --co-snap-border: rgba(255, 247, 230, 0.32);
  }

  /* ── Settle entrance staircase (used by every step) ───────────────── */
  @keyframes _co_settle {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  ._co_onb_settle_1 { animation: _co_settle 800ms cubic-bezier(0.2,0,0,1) both; animation-delay:  60ms; }
  ._co_onb_settle_2 { animation: _co_settle 800ms cubic-bezier(0.2,0,0,1) both; animation-delay: 220ms; }
  ._co_onb_settle_3 { animation: _co_settle 800ms cubic-bezier(0.2,0,0,1) both; animation-delay: 380ms; }
  ._co_onb_settle_4 { animation: _co_settle 800ms cubic-bezier(0.2,0,0,1) both; animation-delay: 540ms; }

  /* ── Eyebrow dot pulse (used by StepHeader) ───────────────────────── */
  @keyframes _co_pulse_dot {
    0%, 100% { opacity: 0.45; }
    50%      { opacity: 1; }
  }
  ._co_onb_dot { animation: _co_pulse_dot 2.4s ease-in-out infinite; }

  /* ── Drag instruction arrow nudge (step 2) ────────────────────────── */
  @keyframes _co_drag_arrow_nudge {
    0%, 100% { transform: translateY(0); opacity: 0.85; }
    50%      { transform: translateY(3px); opacity: 1; }
  }
  ._co_onb_drag_arrow {
    animation: _co_drag_arrow_nudge 1.6s ease-in-out infinite;
  }

  /* ── Step 2 directional wayfinding ────────────────────────────────────
     The Bring-It-In step docks in the top-right. A soft rust glow
     anchored at the bottom-left of the tile stage breathes toward
     the open canvas, while concentric ripples expand from behind
     the file tile to read as "grab this". The pill arrow tracks the
     same down-left direction so verbal and visual cues reinforce. */

  @keyframes _co_onb_drift_arrow_dl {
    0%, 100% { transform: translate(0, 0); opacity: 0.85; }
    50%      { transform: translate(-2px, 2px); opacity: 1; }
  }
  ._co_onb_drag_arrow_dl {
    animation: _co_onb_drift_arrow_dl 1.85s ease-in-out infinite;
  }

  @keyframes _co_onb_drift_glow {
    0%, 100% { opacity: 0.55; transform: scale(0.92); }
    50%      { opacity: 1;    transform: scale(1.08); }
  }
  ._co_onb_drift_glow {
    animation: _co_onb_drift_glow 1.85s ease-in-out infinite;
  }

  /* Ripple — concentric rings emanate from behind the draggable tile.
     Two rings phased half a cycle apart so there's always one mid-
     expansion, creating the sense of continuous outward motion. */
  @keyframes _co_onb_ripple {
    0% {
      transform: scale(0.7);
      opacity: 0;
    }
    20% { opacity: 0.55; }
    100% {
      transform: scale(1.55);
      opacity: 0;
    }
  }
  ._co_onb_ripple {
    position: absolute;
    inset: 0;
    border-radius: 22px;
    border: 1.5px solid var(--co-rust);
    box-shadow: 0 0 0 1px rgba(176, 87, 49, 0.18) inset;
    opacity: 0;
    animation: _co_onb_ripple 2.2s cubic-bezier(0.2, 0, 0, 1) infinite;
    pointer-events: none;
  }
  ._co_onb_ripple_late {
    animation-delay: 1.1s;
  }

  /* Pill hover affordance — when the user mouses over the draggable
     file pill we lift, tilt, and shadow it (a "pick me up" gesture)
     and run a single brief wiggle on hover-in to telegraph that the
     pill is grabbable. Uses !important to override the inline style
     transform/transition baseline set in DraggableFilePill. */
  @keyframes _co_onb_pill_wiggle {
    0%   { transform: translateY(-6px) rotate(-3deg) scale(1.04); }
    25%  { transform: translateY(-7px) rotate(-6deg) scale(1.05); }
    55%  { transform: translateY(-7px) rotate( 1deg) scale(1.05); }
    100% { transform: translateY(-6px) rotate(-3deg) scale(1.04); }
  }
  ._co_onb_drag_pill {
    transition:
      transform 220ms cubic-bezier(0.2, 0, 0, 1),
      box-shadow 220ms cubic-bezier(0.2, 0, 0, 1),
      border-color 220ms cubic-bezier(0.2, 0, 0, 1) !important;
  }
  ._co_onb_drag_pill:hover {
    transform: translateY(-6px) rotate(-3deg) scale(1.04) !important;
    border-color: var(--co-rust) !important;
    box-shadow:
      0 14px 28px rgba(15, 12, 8, 0.18),
      0 4px 8px rgba(15, 12, 8, 0.10) !important;
    animation: _co_onb_pill_wiggle 520ms cubic-bezier(0.2, 0, 0, 1) 1;
  }
  ._co_onb_drag_pill:active {
    transform: translateY(-4px) rotate(-2deg) scale(1.02) !important;
  }
  @media (prefers-reduced-motion: reduce) {
    ._co_onb_drag_pill:hover {
      animation: none !important;
      transform: translateY(-2px) !important;
    }
  }

  /* Click-and-drag cursor demo — loops a virtual cursor in from the
     upper RIGHT of the file pill, presses down on it (scale pulse +
     ring), then drags LEFT toward the canvas (the card is right-
     anchored, canvas opens to the left), then resets. Used in
     DragBriefStep to teach the gesture for users who don't intuit it.
     The cursor is absolutely positioned relative to the pill wrapper
     so the translate distances are pill-relative (origin at pill
     upper-left, pill is 148px square). */
  @keyframes _co_onb_drag_cursor {
    0%   { transform: translate(170px, -10px) scale(1);    opacity: 0; }
    6%   { transform: translate(170px, -10px) scale(1);    opacity: 1; }
    18%  { transform: translate(108px,  44px) scale(1);    opacity: 1; }
    24%  { transform: translate(108px,  44px) scale(0.86); opacity: 1; }
    62%  { transform: translate(-72px,  64px) scale(0.86); opacity: 1; }
    70%  { transform: translate(-72px,  64px) scale(1);    opacity: 1; }
    82%  { transform: translate(-72px,  64px) scale(1);    opacity: 0; }
    100% { transform: translate(170px, -10px) scale(1);    opacity: 0; }
  }
  ._co_onb_drag_cursor {
    position: absolute;
    left: 50%;
    top: 50%;
    margin-left: -74px; /* pill is 148; align cursor origin to pill upper-left */
    margin-top: -74px;
    width: 42px;
    height: 42px;
    pointer-events: none;
    z-index: 3;
    filter: drop-shadow(0 2px 4px rgba(0,0,0,0.22));
    /* Theme-aware: dark cursor on light canvas, light cursor on dark
       canvas, with opposite-tone stroke so the outline always reads. */
    color: #1A1A16;
    --co-cursor-stroke: #F3EFE6;
    animation: _co_onb_drag_cursor 3.6s cubic-bezier(0.45, 0, 0.2, 1) infinite;
  }
  .dark ._co_onb_drag_cursor {
    color: #F3EFE6;
    --co-cursor-stroke: #1A1A16;
  }
  /* Click ring — pulses out from the cursor at the press point. Phased
     to the click moment in the cursor keyframes (~22-30% of cycle). */
  @keyframes _co_onb_drag_click_ring {
    0%, 18% { transform: scale(0.4); opacity: 0; }
    24%     { transform: scale(0.4); opacity: 0.7; }
    36%     { transform: scale(1.35); opacity: 0; }
    100%    { transform: scale(1.35); opacity: 0; }
  }
  ._co_onb_drag_click_ring {
    position: absolute;
    left: 50%;
    top: 50%;
    margin-left: 34px;  /* center on the press point: pill upper-left + (108,44) */
    margin-top: -30px;
    width: 36px;
    height: 36px;
    border-radius: 999px;
    border: 2px solid var(--co-rust);
    pointer-events: none;
    opacity: 0;
    z-index: 2;
    animation: _co_onb_drag_click_ring 3.6s cubic-bezier(0.2, 0, 0, 1) infinite;
  }

  /* Drop-zone border pulse — runs on the dashed canvas-space target
     (BriefDropTarget) while the user is actively dragging the brief
     pill, so the outline "breathes" toward them as a live drop cue. */
  @keyframes _co_onb_drop_pulse {
    0%, 100% { stroke-width: 6px;  stroke-opacity: 0.65; }
    50%      { stroke-width: 10px; stroke-opacity: 1; }
  }
  ._co_onb_drop_pulse {
    animation: _co_onb_drop_pulse 1.3s ease-in-out infinite;
  }
  /* The target comes up with the card. */
  @keyframes _co_onb_drop_in {
    from { opacity: 0; }
  }

  @media (prefers-reduced-motion: reduce) {
    ._co_onb_drag_arrow_dl,
    ._co_onb_drift_glow,
    ._co_onb_ripple,
    ._co_onb_drag_cursor,
    ._co_onb_drag_click_ring,
    ._co_onb_drop_pulse {
      animation: none !important;
    }
  }

  /* ── Step 1 idle invitations: logo float + slot breathe ───────────── */
  @keyframes _co_mark_float {
    0%, 100% { transform: translateY(0); }
    50%      { transform: translateY(-3px); }
  }
  ._co_onb_mark_idle { animation: _co_mark_float 6s ease-in-out infinite; }

  @keyframes _co_slot_breathe {
    0%, 100% { opacity: 0.85; }
    50%      { opacity: 1; }
  }
  ._co_onb_slot_idle { animation: _co_slot_breathe 3s ease-in-out infinite; }

  /* ── Track dots (logo → slot in step 1) ───────────────────────────── */
  ._co_onb_track {
    background-image: radial-gradient(circle, var(--co-track) 1px, transparent 1.5px);
    background-size: 8px 8px;
    background-repeat: repeat-x;
    background-position: 0 50%;
  }

  /* ── Drop animation — fires once when phase flips to 'snapped'.
        The mark gives a satisfying click-into-place pop with a spring
        overshoot, paired with two ring pulses radiating out from the
        slot like a sonar ping. */
  @keyframes _co_drop_pop {
    0%   { transform: scale(0.62); opacity: 0; }
    35%  { transform: scale(1.18); opacity: 1; }
    60%  { transform: scale(0.94); }
    82%  { transform: scale(1.04); }
    100% { transform: scale(1.00); }
  }
  ._co_onb_drop_mark {
    animation: _co_drop_pop 720ms cubic-bezier(0.5, 1.4, 0.4, 1) both;
    transform-origin: 50% 50%;
  }
  @keyframes _co_drop_ring {
    0%   { transform: scale(0.55); opacity: 0.55; }
    70%  { opacity: 0.10; }
    100% { transform: scale(2.10); opacity: 0; }
  }
  ._co_onb_drop_ring {
    position: absolute;
    inset: 0;
    border-radius: 16px;
    border: 1.5px solid var(--co-snap-ring);
    pointer-events: none;
    transform-origin: 50% 50%;
    animation: _co_drop_ring 760ms cubic-bezier(0.2, 0, 0, 1) both;
  }
  ._co_onb_drop_ring._co_onb_drop_ring_late {
    animation-delay: 140ms;
    animation-duration: 820ms;
  }

  /* ── Finish-onboarding celebration ────────────────────────────────
        Plays once when the user clicks "Finish onboarding" on the
        last step. Three layered beats:
          - button does a quick pop+settle (._co_onb_finish_pop)
          - a check glyph draws in over 380ms (._co_onb_check_draw)
          - two staggered rust rings radiate out, mirroring the
            step-1 sonar rings to bookend the experience.
        Total beat ~900ms; orchestrator schedules onFinish to fire
        just after, so the standard step-exit animation reads as
        the natural follow-through. */
  @keyframes _co_finish_pop {
    0%   { scale: 1; }
    35%  { scale: 1.08; }
    70%  { scale: 0.96; }
    100% { scale: 1.00; }
  }
  ._co_onb_finish_pop {
    animation: _co_finish_pop 520ms cubic-bezier(0.5, 1.4, 0.4, 1) both;
  }
  @keyframes _co_check_draw {
    from { stroke-dashoffset: 28; opacity: 0; }
    35%  { opacity: 1; }
    to   { stroke-dashoffset: 0; opacity: 1; }
  }
  ._co_onb_check_draw path {
    stroke-dasharray: 28;
    animation: _co_check_draw 380ms cubic-bezier(0.2, 0, 0, 1) both;
    animation-delay: 120ms;
  }
  @keyframes _co_finish_ring {
    0%   { transform: scale(0.62); opacity: 0.55; }
    70%  { opacity: 0.10; }
    100% { transform: scale(2.40); opacity: 0; }
  }
  ._co_onb_finish_ring {
    position: absolute;
    inset: 0;
    border-radius: 999px;
    border: 1.5px solid var(--co-rust);
    pointer-events: none;
    transform-origin: 50% 50%;
    animation: _co_finish_ring 760ms cubic-bezier(0.2, 0, 0, 1) both;
  }
  ._co_onb_finish_ring._co_onb_finish_ring_late {
    animation-delay: 140ms;
    animation-duration: 820ms;
  }

  /* ── Step transition exit ────────────────────────────────────────── */
  @keyframes _co_step_exit {
    to { opacity: 0; transform: translateY(-8px); filter: blur(2px); }
  }
  ._co_onb_step_exit {
    animation: _co_step_exit 320ms cubic-bezier(0.4, 0, 1, 1) both;
  }

  /* ── Primary rust pill CTA (used by step 2 and beyond) ───────────── */
  ._co_onb_finish {
    appearance: none;
    border: none;
    font-family: inherit;
    cursor: pointer;
    pointer-events: all;
    min-height: 44px;
    padding: 0 28px;
    border-radius: 999px;
    background: var(--co-rust);
    color: var(--co-cta-fg);
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.005em;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    box-shadow: var(--co-cta-shadow);
    transition:
      background-color 320ms cubic-bezier(0.5, 1.5, 0.4, 1),
      scale 320ms cubic-bezier(0.5, 1.5, 0.4, 1);
  }
  ._co_onb_finish:hover  { background-color: #A75F3D; scale: 1.03; }
  ._co_onb_finish:active {
    transition: scale 200ms cubic-bezier(0.4, 0, 0.2, 1);
    scale: 0.94;
  }
  ._co_onb_finish:focus-visible {
    outline: 2px solid var(--co-rust);
    outline-offset: 2px;
  }

  /* ── Dev-only restart affordance ──────────────────────────────────── */
  ._co_onb_restart {
    appearance: none;
    background: transparent;
    border: none;
    padding: 4px 8px;
    margin: 0;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: ui-monospace, 'JetBrains Mono', 'SF Mono', Menlo, monospace;
    font-size: 10px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--co-fg-muted);
    opacity: 0.55;
    cursor: pointer;
    /* Explicit pointer-events so the button still receives clicks
       inside widget contexts where parents (or React Flow internals)
       may set pointer-events: none on container nodes. */
    pointer-events: all;
    border-radius: 4px;
    transition: opacity 150ms cubic-bezier(0.2,0,0,1), color 150ms cubic-bezier(0.2,0,0,1);
  }
  ._co_onb_restart:hover { opacity: 1; color: var(--co-fg); }
  ._co_onb_restart:focus-visible {
    outline: 2px solid var(--co-rust);
    outline-offset: 2px;
  }
  ._co_onb_restart svg { display: block; }

  @media (prefers-reduced-motion: reduce) {
    ._co_onb_settle_1, ._co_onb_settle_2, ._co_onb_settle_3,
    ._co_onb_settle_4, ._co_onb_dot,
    ._co_onb_mark_idle, ._co_onb_slot_idle,
    ._co_onb_drop_mark, ._co_onb_drop_ring,
    ._co_onb_finish_pop, ._co_onb_check_draw path,
    ._co_onb_finish_ring,
    ._co_onb_step_exit { animation: none; }
  }
`;
